import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ASSETS as SEED_ASSETS, CLAIMS, CLAIM_LABEL, checkClaimGate,
  type Asset, type AssetVersion, type Annotation,
} from '../data/studio';
import { BACKEND } from '../lib/db';
import { fetchAssets, fromSimpleSeed } from '../lib/studioLive';
import { onFail } from '../lib/errors';
import { TENANT } from '@tenant';
import './studio.css';
import { ReviewPanel } from '../components/ReviewPanel';
import { NewAsset } from '../components/CreateForms';

/* =====================================================================================
   Studio — marketing assets and Medical, Legal and Regulatory review.

   Three things this does that a shared folder cannot:

   1. The claim gate runs BEFORE human review and blocks submission. An RUO product cannot
      be promoted with clinical utility claims, and the system refuses rather than relying
      on a reviewer catching it.
   2. Approved versions are immutable. Editing produces a new version; the approved one is
      frozen with its annotations and certification intact.
   3. Unresolved blockers carry forward automatically to the next version, so nothing is
      lost silently between rounds.

   The proof viewer here renders a placeholder page box. In production this is a PDF.js
   canvas over the flattened rendition, with Office documents converted on upload. The
   annotation layer, coordinate model and interaction are the real thing: pins are stored
   normalised 0..1 against the page box so they land correctly at any zoom or device pixel
   ratio.
   ===================================================================================== */

const DISC_COLOUR: Record<string, string> = {
  medical: 'var(--series-5)', legal: 'var(--series-3)',
  regulatory: 'var(--series-4)', commercial: 'var(--series-2)', brand: 'var(--series-6)',
};

/* Live tenants read studio.v_assets; the seed path is unchanged for demonstrations. */
function useAssets(): [Asset[], () => void] {
  const [rows, setRows] = useState<Asset[]>([]);
  const reload = useCallback(() => {
    if (BACKEND !== 'supabase') return;
    fetchAssets().then(setRows).catch(onFail('Studio assets', () => setRows([])));
  }, []);
  useEffect(() => { reload(); }, [reload]);
  /* Without a database: the rich seed if the tenant has one (Scira's worked examples), else
     the tenant's own short-form asset seed lifted into shape. */
  const local = useMemo(() => SEED_ASSETS.length ? SEED_ASSETS
    : fromSimpleSeed(TENANT.seed.asset, TENANT.brand.orgName), []);
  return BACKEND === 'supabase' ? [rows, reload] : [local, () => {}];
}

export function StudioView() {
  const [creating, setCreating] = useState(false);
  const [tab, setTab] = useState<'assets' | 'claims'>('assets');
  const [open, setOpen] = useState<Asset | null>(null);
  const [ASSETS, reload] = useAssets();

  const stats = useMemo(() => {
    const inReview = ASSETS.filter((a) => a.status === 'in_review').length;
    const blockers = ASSETS.flatMap((a) =>
      a.versions[0].annotations.filter((n) => n.severity === 'blocker' && n.status === 'open'),
    ).length;
    const gateFails = ASSETS.filter((a) => checkClaimGate(a, a.versions[0]).length > 0).length;
    return { total: ASSETS.length, inReview, blockers, gateFails };
  }, [ASSETS]);

  return (
    <div className="page studio">
      <div className="page-head">
        <h1 className="page-title">Studio</h1>
        <p className="page-sub">
          Marketing material and its medical, legal and regulatory review. Approved versions are
          immutable, unresolved blockers carry forward, and an asset cannot be approved if it claims
          more than the product's regulatory status allows.
        </p>
      </div>

      {ASSETS.length === 0 && (
        <p className="module-empty">No marketing assets yet. Create one, upload the proof, and assign medical, legal and regulatory reviewers separately.</p>
      )}

      <div className="toolbar">
        <div className="seg">
          <button className={tab === 'assets' ? 'on' : ''} onClick={() => setTab('assets')}>Assets</button>
          <button className={tab === 'claims' ? 'on' : ''} onClick={() => setTab('claims')}>Claims library</button>
        </div>
        <button className="btn primary" style={{ marginLeft: 'auto' }}
                onClick={() => setCreating(true)}>New asset</button>
      </div>

      <div className="metrics">
        <div className="metric card"><div className="label">Assets</div><div className="metric-value">{stats.total}</div></div>
        <div className="metric card"><div className="label">In review</div><div className="metric-value">{stats.inReview}</div></div>
        <div className="metric card" style={stats.blockers ? { borderColor: 'var(--risk)', background: 'var(--risk-soft)' } : undefined}>
          <div className="label">Open blockers</div>
          <div className="metric-value" style={stats.blockers ? { color: 'var(--risk)' } : undefined}>{stats.blockers}</div>
        </div>
        <div className="metric card" style={stats.gateFails ? { borderColor: 'var(--warn)', background: 'var(--warn-soft)' } : undefined}>
          <div className="label">Failing the claim gate</div>
          <div className="metric-value" style={stats.gateFails ? { color: 'var(--warn)' } : undefined}>{stats.gateFails}</div>
        </div>
      </div>

      {tab === 'assets' ? (
        <div className="asset-grid">
          {ASSETS.map((a) => {
            const v = a.versions[0];
            const gate = checkClaimGate(a, v);
            const blockers = v.annotations.filter((n) => n.severity === 'blocker' && n.status === 'open').length;
            const pending = v.reviewers.filter((r) => r.required && !r.decision).length;
            return (
              <button key={a.id} className={`card asset ${gate.length ? 'gate-fail' : ''}`} onClick={() => setOpen(a)}>
                <div className="asset-head">
                  <span className="mono asset-job">{a.jobCode}</span>
                  <span className={`pill ${
                    a.status === 'effective' ? 'ok'
                      : a.status === 'in_review' ? 'info'
                      : a.status === 'changes_required' ? 'warn' : 'mute'}`}>
                    {a.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="asset-title">{a.title}</div>
                <div className="asset-meta">
                  {a.type.replace('_', ' ')} · {v.label} · {v.pages} pages
                </div>
                <div className="asset-terr">
                  {a.territories.map((t) => <span key={t} className="terr-chip">{t}</span>)}
                  <span className="pill mute">{CLAIM_LABEL[v.claimLevel]}</span>
                </div>
                <div className="asset-foot">
                  {gate.length > 0 && <span className="pill risk">gate blocked</span>}
                  {blockers > 0 && <span className="pill risk">{blockers} blocker{blockers > 1 ? 's' : ''}</span>}
                  {pending > 0 && <span className="pill info">{pending} awaiting</span>}
                  {v.locked && <span className="pill ok">locked</span>}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="card table-wrap">
          <table className="data">
            <thead>
              <tr><th>Code</th><th>Claim</th><th>Level</th><th>Status</th><th>Substantiation</th></tr>
            </thead>
            <tbody>
              {CLAIMS.map((c) => (
                <tr key={c.code}>
                  <td className="mono">{c.code}</td>
                  <td style={{ maxWidth: '46ch' }}>{c.text}</td>
                  <td><span className="pill mute">{CLAIM_LABEL[c.level]}</span></td>
                  <td>
                    <span className={`pill ${c.status === 'approved' ? 'ok' : c.status === 'in_review' ? 'info' : 'mute'}`}>
                      {c.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    {c.references.map((r) => (
                      <div key={r.citation} className="cell-sub" style={{ maxWidth: '38ch' }}>
                        <span className={`pill ${r.strength === 'primary' ? 'ok' : 'mute'}`}>{r.strength}</span>{' '}
                        {r.citation}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="table-note">
            A claim approved once with its references is reused across every asset, which is what
            keeps MLR cycles short. Companies that skip the claims library spend every round
            re-arguing the same sentence.
          </div>
        </div>
      )}

      {open && <ProofReview asset={open} onClose={() => setOpen(null)} />}
      {creating && <NewAsset onClose={() => setCreating(false)} onDone={() => { setCreating(false); reload(); }} />}

    </div>
  );
}

/* =====================================================================================
   Proof review: document pane with pinned annotations, plus the review sidebar.
   ===================================================================================== */

function ProofReview({ asset, onClose }: { asset: Asset; onClose: () => void }) {
  const [versionN, setVersionN] = useState(asset.versions[0].n);
  const version = asset.versions.find((v) => v.n === versionN)!;
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Annotation | null>(null);
  const [placing, setPlacing] = useState(false);
  const [draft, setDraft] = useState<{ x: number; y: number } | null>(null);

  const gate = checkClaimGate(asset, version);
  const pageAnnotations = version.annotations.filter((a) => a.page === page);
  const openBlockers = version.annotations.filter((a) => a.severity === 'blocker' && a.status === 'open');

  function placePin(e: React.MouseEvent<HTMLDivElement>) {
    if (!placing || version.locked) return;
    const r = e.currentTarget.getBoundingClientRect();
    setDraft({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
    setPlacing(false);
  }

  return (
    <div className="proof-overlay">
      <header className="proof-bar">
        <div className="proof-ident">
          <span className="mono proof-job">{asset.jobCode}</span>
          <span className="proof-title">{asset.title}</span>
          <select className="input sel proof-ver" value={versionN} onChange={(e) => { setVersionN(Number(e.target.value)); setPage(1); setSelected(null); }}>
            {asset.versions.map((v) => (
              <option key={v.n} value={v.n}>{v.label} · {v.status.replace('_', ' ')}</option>
            ))}
          </select>
          {version.locked && <span className="pill ok">Locked, approved</span>}
        </div>
        <div className="proof-actions">
          <button className="btn sm" disabled={version.locked} onClick={() => setPlacing((p) => !p)}>
            {placing ? 'Click the proof…' : 'Add comment'}
          </button>
          <button className="btn sm">Download</button>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      </header>

      <div className="proof-body">
        <div className="proof-canvas">
          {gate.length > 0 && (
            <div className="gate-banner">
              <div className="gate-title">Claim gate failed. Approval is blocked.</div>
              <ul>{gate.map((g) => <li key={g.territory + g.product}>{g.message}</li>)}</ul>
              <div className="gate-fix">
                Reduce the claim level, remove the affected territory, or change the product's
                regulatory status if it has genuinely changed. This is a hard block, not a warning.
              </div>
            </div>
          )}

          <div className={`page-stage ${placing ? 'placing' : ''}`} onClick={placePin}>
            {/* Placeholder for the PDF.js canvas. Layout mimics a rendered page so the
                annotation coordinate model is exercised for real. */}
            <div className="page-sheet">
              <div className="sheet-brand">{TENANT.brand.orgName.toUpperCase()}</div>
              <div className="sheet-h1">{asset.title}</div>
              <div className="sheet-rule" />
              <div className="sheet-body">
                <div className="sheet-line w90" /><div className="sheet-line w75" />
                <div className="sheet-line w82" /><div className="sheet-line w60" />
              </div>
              <div className="sheet-block" />
              <div className="sheet-body">
                <div className="sheet-line w88" /><div className="sheet-line w70" />
                <div className="sheet-line w79" />
              </div>
              <div className="sheet-foot">
                {/* The footer line the gate would require, from the version's own verdict, or
                    the date. It used to print an RUO statement, which is one industry's
                    disclaimer, whenever a particular Scira claim code was on the version. */}
                {version.gate?.length
                  ? `Claim gate: ${version.gate.length} violation${version.gate.length === 1 ? '' : 's'}`
                  : `Date of preparation: ${new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`} · {asset.jobCode}
              </div>

              {pageAnnotations.map((a, i) => (
                <button
                  key={a.id}
                  className={`pin sev-${a.severity} ${selected?.id === a.id ? 'sel' : ''} ${a.status !== 'open' ? 'resolved' : ''}`}
                  style={{
                    left: `${a.x * 100}%`, top: `${a.y * 100}%`,
                    borderColor: DISC_COLOUR[a.discipline],
                  }}
                  onClick={(e) => { e.stopPropagation(); setSelected(a); }}
                  title={`${a.author}: ${a.body}`}
                >
                  {i + 1}
                </button>
              ))}

              {draft && (
                <div className="pin draft" style={{ left: `${draft.x * 100}%`, top: `${draft.y * 100}%` }}>+</div>
              )}
            </div>

            {version.pages > 1 && (
              <div className="pager">
                {Array.from({ length: version.pages }, (_, i) => i + 1).map((p) => (
                  <button key={p} className={p === page ? 'on' : ''} onClick={(e) => { e.stopPropagation(); setPage(p); }}>
                    {p}
                  </button>
                ))}
              </div>
            )}
          </div>

          {draft && (
            <div className="card draft-comment">
              <div className="label">New comment at page {page}</div>
              <textarea className="input draft-text" placeholder="What needs to change, and why?" rows={3} />
              <div className="draft-row">
                <select className="input sel"><option>Blocker</option><option>Major</option><option>Minor</option><option>Comment</option></select>
                <button className="btn sm" onClick={() => setDraft(null)}>Cancel</button>
                <button className="btn sm primary" onClick={() => setDraft(null)}>Add</button>
              </div>
            </div>
          )}
        </div>

        <aside className="proof-side">
          <section className="side-block">
            <div className="label">Review round</div>
            {version.reviewers.length === 0 ? (
              <p className="drawer-text">Not yet submitted for review.</p>
            ) : (
              <div className="reviewers">
                {version.reviewers.map((r) => (
                  <div key={r.name} className="reviewer">
                    <span className="disc-dot" style={{ background: DISC_COLOUR[r.discipline] }} />
                    <div className="reviewer-main">
                      <div className="reviewer-name">
                        {r.name}
                        {r.certifier && <span className="pill accent" style={{ marginLeft: 6 }}>certifier</span>}
                      </div>
                      <div className="reviewer-disc">{r.discipline}{r.required ? ' · required' : ''}</div>
                    </div>
                    <span className={`pill ${
                      !r.decision ? 'mute'
                        : r.decision === 'approved' ? 'ok'
                        : r.decision === 'approved_with_comments' ? 'info' : 'risk'}`}>
                      {r.decision ? r.decision.replace(/_/g, ' ') : 'awaiting'}
                    </span>
                  </div>
                ))}
              </div>
            )}
            {openBlockers.length > 0 && (
              <div className="side-warn">
                {openBlockers.length} open blocker{openBlockers.length > 1 ? 's' : ''}. These carry
                forward to the next version automatically if not resolved.
              </div>
            )}
          </section>

          <section className="side-block">
            {/* Files, comments and MLR assignment in one place. Works on DOCX and PPTX as
                well as PDF: the comment thread does not require a rendered page. */}
            <ReviewPanel assetId={String(asset.id)} versionId={null} />

            <div className="label">Comments ({version.annotations.length})</div>
            <div className="comments">
              {version.annotations.length === 0 && <p className="drawer-text">No comments on this version.</p>}
              {version.annotations.map((a) => (
                <button
                  key={a.id}
                  className={`comment ${selected?.id === a.id ? 'sel' : ''}`}
                  onClick={() => { setPage(a.page); setSelected(a); }}
                >
                  <div className="comment-head">
                    <span className="disc-dot" style={{ background: DISC_COLOUR[a.discipline] }} />
                    <span className="comment-author">{a.author}</span>
                    <span className={`pill ${
                      a.severity === 'blocker' ? 'risk'
                        : a.severity === 'major' ? 'warn'
                        : a.severity === 'minor' ? 'info' : 'mute'}`}>
                      {a.severity}
                    </span>
                  </div>
                  {a.carriedForward && <div className="carried">Carried forward from the previous version</div>}
                  <p className="comment-body">{a.body}</p>
                  {a.replies?.map((r) => (
                    <div key={r.body} className="reply">
                      <strong>{r.author}</strong> {r.body}
                    </div>
                  ))}
                  <div className="comment-foot">
                    page {a.page} · {a.createdAt} ·{' '}
                    <span className={a.status === 'open' ? 'open' : 'done'}>{a.status}</span>
                  </div>
                </button>
              ))}
            </div>
          </section>

          <section className="side-block">
            <div className="label">Decision</div>
            {version.locked ? (
              <div className="side-locked">
                Approved and locked on {version.decidedAt}. This version cannot be edited. Create a
                new version to make changes.
              </div>
            ) : gate.length > 0 ? (
              <div className="side-warn">
                Submission is blocked by the claim gate. Resolve the violations above first.
              </div>
            ) : (
              /* The decision controls live in ReviewPanel above, which records the reviewer,
                 the meaning of the signature and the version against it. This row was a second,
                 unwired copy of the same three buttons on the same screen: it looked live and
                 did nothing. Removed rather than duplicated, so there is one implementation. */
              <div className="drawer-note">
                Record your decision in the review panel above. Approving stamps your name, role
                and signature meaning against this version and cannot be undone.
              </div>
            )}
          </section>

          {version.changeSummary && (
            <section className="side-block">
              <div className="label">Change summary</div>
              <p className="drawer-text">{version.changeSummary}</p>
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

export type { AssetVersion };
